import type { ReactNode } from 'react';
import { clsx } from '../../../ds/clsx';
import { WizardButton } from './controls';
import styles from './WizardFooter.module.css';

export interface WizardFooterAction {
  label: string;
  onClick?: (() => void) | undefined;
  disabled?: boolean | undefined;
  loading?: boolean | undefined;
  /** `submit` for the primary button of a form step. */
  type?: 'button' | 'submit' | undefined;
  /** Id of the form the submit button belongs to. */
  form?: string | undefined;
}

export interface WizardFooterProps {
  /** Outline „Zurück" on the left. */
  back?: WizardFooterAction | undefined;
  /** Subtle text on the left when there is no back button („Alles später in den Einstellungen änderbar."). */
  note?: ReactNode | undefined;
  /** Text button before the primary one („Überspringen", „Widerruf später", „Allein starten"). */
  secondary?: WizardFooterAction | undefined;
  /** Muted sentence before the primary one („Ohne bestätigte USt-ID wird mit Umsatzsteuer abgerechnet."). */
  hint?: ReactNode | undefined;
  primary: WizardFooterAction;
}

/** The footer row of the wizard steps (Admin 03–10, Nutzer 02–11). */
export function WizardFooter({ back, note, secondary, hint, primary }: WizardFooterProps) {
  const rightStart = hint ? 'hint' : secondary ? 'secondary' : 'primary';
  return (
    <div className={styles.footer}>
      {back ? (
        <WizardButton variant="secondary" onClick={back.onClick} disabled={back.disabled}>
          {back.label}
        </WizardButton>
      ) : null}
      {note ? <span className={styles.note}>{note}</span> : null}
      {hint ? <span className={clsx(styles.hint, rightStart === 'hint' && styles.push)}>{hint}</span> : null}
      {secondary ? (
        <WizardButton
          variant="ghost"
          className={clsx(rightStart === 'secondary' && styles.push)}
          onClick={secondary.onClick}
          disabled={secondary.disabled}
          loading={secondary.loading}
        >
          {secondary.label}
        </WizardButton>
      ) : null}
      <WizardButton
        variant="primary"
        type={primary.type ?? 'button'}
        form={primary.form}
        className={clsx(rightStart === 'primary' && styles.push)}
        onClick={primary.onClick}
        disabled={primary.disabled}
        loading={primary.loading}
      >
        {primary.label}
      </WizardButton>
    </div>
  );
}
